import { useForm } from 'react-hook-form';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { useClientQueries } from '@zenstackhq/tanstack-query/react';
import { loginSchema, type LoginInput } from '@zsp/shared';
import { schema } from '../../__generated__/schema-lite';
import { useAuth } from '../../auth/AuthContext';
import { zodFormResolver } from '../../lib/zodFormResolver';
import { translateError } from '../../lib/translate-error';
import { useTranslation } from 'react-i18next';

/** Login page ViewModel: owns auth mutation, form, and post-submit navigation.
 *  The page only renders the JSX bound to what's returned here. */
export function useLoginPageVM() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setToken } = useAuth();
  const { t } = useTranslation();
  const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname ?? '/';

  const crud = useClientQueries(schema);
  const login = crud.$procs.login.useMutation();

  const { register, handleSubmit, formState } = useForm<LoginInput>({
    resolver: zodFormResolver<LoginInput>(loginSchema),
  });

  const onSubmit = handleSubmit(async (values) => {
    try {
      const token = await login.mutateAsync({ args: { input: values } });
      setToken(token);
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(translateError(t, err));
    }
  });

  return { register, formState, onSubmit };
}
